import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Title, Text, Card } from '@constants/Themed';
import { FlatList, View, StyleSheet, Pressable, Platform, Dimensions, ScrollView } from 'react-native';
import SalesFunnelList from './Components/SalesFunnelList';
import { RouteProp, useRoute } from '@react-navigation/native';
import apis from '@api/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardNavigatorParamList, SalesStackParamList } from '@navigation/types';
import { Spinner, XStack, YStack, SizableText } from 'tamagui';
import AnimatedLottieView from '@utils/lottieAnimation';
import { MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Alert } from 'react-native';

// Helper function to parse text by delimiter
const parseByDelimiter = (text: string): string => {
  if (!text) return '';
  return text.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const SalesFunnel = ({ navigation, route }) => {
  const { params } = useRoute<RouteProp<SalesStackParamList, 'SalesFunnel'>>();
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const funnelRef = useRef<any>(null);
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Increased default page size
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [isStatsExpanded, setIsStatsExpanded] = useState(true);
  const [selectedStageIndex, setSelectedStageIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'single'>('all');

  const { data: campaignData, isLoading, refetch } = useQuery({
    queryKey: [`campaignData${params?.campaignId ?? ''}`, currentPage, pageSize, viewMode, selectedStageIndex],
    queryFn: async () => {
      const stageIndex = viewMode === 'single' ? selectedStageIndex : undefined;
      const response = await apis.getCampaign(params?.campaignId ?? '', currentPage, pageSize, stageIndex);

      // Process audience_data to maintain stage structure
      const processedAudienceData = response.audience_data?.map((stageLeads, stageIndex) => {
        return stageLeads.map(client => {
          if (!client.data) client.data = {};
          // Add stage information to each lead
          client.stageIndex = stageIndex;
          client.stageId = response.stages?.[stageIndex]?._id;

          // Combine tracker from campaigns into the clients
          if (response.tracker) {
            client.campaigns?.forEach(campaign => {
              if (Object.keys(response.tracker).includes(campaign)) {
                client.data = { ...client.data, tracker: response.tracker[campaign] };
              }
            });
          }
          return client;
        });
      }) || [];

      // Flatten for backward compatibility but preserve stage info
      const allLeads = processedAudienceData.flat();
      setClients(allLeads);
      setPaginationInfo(response.pagination);

      return response;
    },
      refetchOnWindowFocus: true,
      retry: 1,
      refetchOnMount: 'always',
    }
  );

  // Mutations for funnel operations
  const moveLeadMutation = useMutation({
    mutationFn: (variables: { leadId: string; fromStage: string; toStage: string }) =>
      (apis as any).moveLeadToStage(variables.leadId, variables.fromStage, variables.toStage, params?.campaignId ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`campaignData${params?.campaignId}`] });
      Alert.alert('Success', 'Lead moved successfully');
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to move lead');
      console.error('Error moving lead:', error);
    }
  });

  const updateLeadMutation = useMutation({
    mutationFn: (variables: { leadId: string; leadData: any }) =>
      (apis as any).updateLeadData(variables.leadId, variables.leadData, params?.campaignId ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`campaignData${params?.campaignId}`] });
      Alert.alert('Success', 'Lead updated successfully');
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to update lead');
      console.error('Error updating lead:', error);
    }
  });

  const deleteLeadMutation = useMutation({
    mutationFn: (variables: { leadId: string }) =>
      (apis as any).deleteLeadFromCampaign(variables.leadId, params?.campaignId ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`campaignData${params?.campaignId}`] });
      Alert.alert('Success', 'Lead removed successfully');
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to remove lead');
      console.error('Error removing lead:', error);
    }
  });

  const exportDataMutation = useMutation({
    mutationFn: (variables: { format?: string }) =>
      (apis as any).exportCampaignData(params?.campaignId ?? '', variables.format || 'csv'),
    onSuccess: (data) => {
      Alert.alert('Success', 'Data exported successfully');
      console.log('Export data:', data);
    },
    onError: (error) => {
      Alert.alert('Error', 'Failed to export data');
      console.error('Error exporting data:', error);
    }
  });

  // Dev-only mutations for stage management
  const addStageMutation = useMutation({
    mutationFn: (stageData: any) => {
      console.log('Adding stage with data:', stageData);
      return (apis as any).addCampaignStage(stageData, params?.campaignId ?? '');
    },
    onMutate: async (stageData) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      const queryKey = [`campaignData${params?.campaignId ?? ''}`, currentPage, pageSize, viewMode, selectedStageIndex];
      await queryClient.cancelQueries({ queryKey: [`campaignData${params?.campaignId ?? ''}`] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        const newStage = {
          _id: `temp_${Date.now()}`, // Temporary ID for optimistic update
          title: stageData.title,
          description: stageData.description,
          color: stageData.color,
          leads: [] // Empty leads array for new stage
        };

        // Also add empty audience_data array for the new stage
        const newAudienceData = [...(old.audience_data || [])];
        newAudienceData.push([]);

        return {
          ...old,
          stages: [...(old.stages || []), newStage],
          audience_data: newAudienceData
        };
      });

      // Return a context object with the snapshotted value
      return { previousData, queryKey };
    },
    onSuccess: (data: any) => {
      console.log('Stage added successfully:', data);
      // Invalidate and refetch to get fresh data
      queryClient.invalidateQueries({ queryKey: [`campaignData${params?.campaignId ?? ''}`] });
      Alert.alert('Success', 'Stage added successfully');
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      Alert.alert('Error', `Failed to add stage: ${error?.message || 'Unknown error'}`);
      console.error('Error adding stage:', error);
    }
  });

  const deleteStageMutation = useMutation({
    mutationFn: (stageId: string) => {
      console.log('Deleting stage with ID:', stageId);
      return (apis as any).deleteCampaignStage(stageId, params?.campaignId ?? '');
    },
    onMutate: async (stageId) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      const queryKey = [`campaignData${params?.campaignId ?? ''}`, currentPage, pageSize, viewMode, selectedStageIndex];
      await queryClient.cancelQueries({ queryKey: [`campaignData${params?.campaignId ?? ''}`] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update to remove the stage
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        const stageIndex = (old.stages || []).findIndex((stage: any) => stage._id === stageId);
        if (stageIndex === -1) return old;

        // Remove stage and corresponding audience_data
        const newStages = (old.stages || []).filter((stage: any) => stage._id !== stageId);
        const newAudienceData = [...(old.audience_data || [])];
        newAudienceData.splice(stageIndex, 1);

        return {
          ...old,
          stages: newStages,
          audience_data: newAudienceData
        };
      });

      // Return a context object with the snapshotted value
      return { previousData, queryKey };
    },
    onSuccess: (data: any) => {
      console.log('Stage deleted successfully:', data);
      // Invalidate and refetch to get fresh data
      queryClient.invalidateQueries({ queryKey: [`campaignData${params?.campaignId ?? ''}`] });
      Alert.alert('Success', 'Stage deleted successfully');
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      Alert.alert('Error', `Failed to delete stage: ${error?.message || 'Unknown error'}`);
      console.error('Error deleting stage:', error);
    }
  });

  const addTestLeadMutation = useMutation({
    mutationFn: (leadData: any) => {
      console.log('Adding test lead with data:', leadData);
      return (apis as any).addLeadToCampaign(leadData, params?.campaignId ?? '', campaignData?.stages?.[0]?._id);
    },
    onMutate: async (leadData) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: [`campaignData${params?.campaignId}`] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`campaignData${params?.campaignId}`]);

      // Optimistically update to add the lead to the first stage
      queryClient.setQueryData([`campaignData${params?.campaignId}`], (old: any) => {
        if (!old || !old.stages?.[0]) return old;

        const newLead = {
          _id: `temp_lead_${Date.now()}`, // Temporary ID for optimistic update
          data: leadData,
          stageIndex: 0, // Add stage info for proper organization
          stageId: old.stages[0]._id
        };

        return {
          ...old,
          audience_data: [
            [...(old.audience_data?.[0] || []), newLead], // Add new lead to first stage
            ...(old.audience_data || []).slice(1) // Keep other stages
          ]
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onSuccess: (data: any) => {
      console.log('Test lead added successfully:', data);
      // Update with real data from server
      queryClient.setQueryData([`campaignData${params?.campaignId}`], (old: any) => {
        if (!old || !data?.campaign) return old;
        return data.campaign;
      });
      Alert.alert('Success', 'Test lead added to first stage');
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData([`campaignData${params?.campaignId}`], context.previousData);
      }
      Alert.alert('Error', 'Failed to add test lead');
      console.error('Error adding test lead:', error);
    }
  });

  useEffect(() => {
    // Cleanup list
    return () => {
      setClients([]);
      funnelRef.current = null;
    };
  }, []);

  // Calculate comprehensive campaign statistics
  const campaignStats = useMemo(() => {
    if (!campaignData) return null;

    const totalLeads = campaignData.audience_data?.flat()?.length || 0;
    const totalStages = campaignData.stages?.length || 0;
    const finalStageLeads = campaignData.audience_data?.[totalStages - 1]?.length || 0;
    const conversionRate = totalLeads > 0 ? ((finalStageLeads / totalLeads) * 100).toFixed(1) : 0;

    // Calculate stage distribution
    const stageDistribution = campaignData.stages?.map((stage, index) => ({
      title: stage.title,
      count: campaignData.audience_data?.[index]?.length || 0,
      percentage: totalLeads > 0 ? (((campaignData.audience_data?.[index]?.length || 0) / totalLeads) * 100).toFixed(1) : 0
    })) || [];

    // Calculate campaign duration
    const startDate = new Date(campaignData.start_date);
    const endDate = new Date(campaignData.end_date);
    const now = new Date();
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const elapsedDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, totalDays - elapsedDays);

    // Calculate progress percentage
    const progressPercentage = totalDays > 0 ? Math.min(100, (elapsedDays / totalDays) * 100).toFixed(1) : 0;

    // Calculate bounce rate
    const totalEmails = campaignData.send_count || 0;
    const bounces = campaignData.bounce_list?.length || 0;
    const bounceRate = totalEmails > 0 ? ((bounces / totalEmails) * 100).toFixed(1) : 0;

    // Revenue metrics
    const figures = campaignData.figures || {};
    const revenue = figures.total_revenue || 0;
    const cost = figures.total_cost || 0;
    const profit = figures.total_profit || 0;
    const roi = figures.total_roi || 0;

    // Target vs actual
    const salesTarget = campaignData.sales_target || 0;
    const actualConversions = figures.total_conversions || finalStageLeads;
    const targetAchievement = salesTarget > 0 ? ((actualConversions / salesTarget) * 100).toFixed(1) : 0;

    return {
      // Basic stats
      totalLeads,
      totalStages,
      conversionRate,
      campaignTitle: campaignData.campaign_title || 'Untitled Campaign',

      // Campaign timeline
      totalDays,
      elapsedDays,
      remainingDays,
      progressPercentage,
      startDate: startDate.toLocaleDateString(),
      endDate: endDate.toLocaleDateString(),

      // Email performance
      totalEmails,
      bounces,
      bounceRate,

      // Financial metrics
      revenue,
      cost,
      profit,
      roi,

      // Target tracking
      salesTarget,
      actualConversions,
      targetAchievement,

      // Stage distribution
      stageDistribution,

      // Campaign metadata
      status: campaignData.status,
      marketingScope: campaignData.marketing_scope,
      description: campaignData.description,
      goals: campaignData.goals,
      tags: campaignData.tags || [],

      // Legacy compatibility
      totalRevenue: revenue,
      totalCost: cost,
      totalConversions: actualConversions,
      sendCount: totalEmails
    };
  }, [campaignData]);

  // Header Component with Campaign Statistics
  const HeaderComponent = useCallback(() => {
    if (!campaignStats) return null;

    const screenHeight = Dimensions.get('window').height;
    const expandedHeight = screenHeight * 0.7;
    const collapsedHeight = 80;

    return (
      <Animatable.View
        animation={isStatsExpanded ? "slideInUp" : "slideInDown"}
        duration={300}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius: isStatsExpanded ? 0 : 20,
          borderBottomRightRadius: isStatsExpanded ? 0 : 20,
          padding: 16,
          paddingBottom: Platform.select({ web: 16, default: 20 }),
          shadowColor: "#000",
          shadowOffset: { width: 0, height: -8 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 20,
          elevation: 10,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
          height: isStatsExpanded ? expandedHeight : collapsedHeight,
          maxHeight: expandedHeight,
          overflow: 'hidden',
          zIndex: 1000
        }}
      >
        <View style={{ flex: 1, flexDirection: 'column', height: '100%' }}>
        {/* Collapsible Header */}
        <Pressable
          onPress={() => setIsStatsExpanded(!isStatsExpanded)}
            style={{ flexDirection: 'row', alignItems: 'center', marginBottom: isStatsExpanded ? 12 : 0, flexShrink: 0 }}
        >
          <View style={{
            backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="campaign"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SizableText style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 4,
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {campaignData?.campaign_title || 'Sales Campaign'}
            </SizableText>
            <SizableText numberOfLines={2} style={{
              color: theme ? "#64748b" : "#94a3b8",
              fontSize: 14,
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {campaignData?.description || 'Campaign performance and lead management'}
            </SizableText>
          </View>
          <View style={{
            backgroundColor: theme ? "#d1fae5" : "#064e3b",
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 20,
            marginRight: 8
          }}>
            <Text style={{
              color: theme ? "#059669" : "#10b981",
              fontSize: 12,
              fontWeight: '600'
            }}>
              {campaignStats.status.toUpperCase()}
            </Text>
          </View>
          <MaterialIcons
              name={isStatsExpanded ? "expand-more" : "expand-less"}
            size={24}
            color={theme ? "#64748b" : "#94a3b8"}
          />
          </Pressable>
        {/* Collapsible Stats Content */}
        {isStatsExpanded && (
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 20 }}
              showsVerticalScrollIndicator={true}
              nestedScrollEnabled={true}
              bounces={true}
              scrollEnabled={true}
            >
            {/* Stage Navigation Controls */}
            <View style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: theme ? "#f8fafc" : "#334155",
              borderRadius: 12,
              borderWidth: 1,
              borderColor: theme ? "#e2e8f0" : "#475569"
            }}>
              <Text style={{
                fontSize: 14,
                fontWeight: '600',
                color: theme ? "#1e293b" : "#f1f5f9",
                marginBottom: 12
              }}>
                Stage Navigation
              </Text>

              {/* View Mode Toggle */}
              <XStack gap={8} marginBottom={12} flexWrap="wrap">
                <Pressable
                  onPress={() => setViewMode('all')}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: viewMode === 'all'
                      ? (theme ? "#3b82f6" : "#60a5fa")
                      : (theme ? "#e2e8f0" : "#475569"),
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <MaterialIcons
                    name="view-module"
                    size={16}
                    color={viewMode === 'all' ? 'white' : (theme ? "#64748b" : "#94a3b8")}
                  />
                  <Text style={{
                    color: viewMode === 'all' ? 'white' : (theme ? "#64748b" : "#94a3b8"),
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    All Stages
                  </Text>
                </Pressable>

                <Pressable
                  onPress={() => setViewMode('single')}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: viewMode === 'single'
                      ? (theme ? "#3b82f6" : "#60a5fa")
                      : (theme ? "#e2e8f0" : "#475569"),
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <MaterialIcons
                    name="view-column"
                    size={16}
                    color={viewMode === 'single' ? 'white' : (theme ? "#64748b" : "#94a3b8")}
                  />
                  <Text style={{
                    color: viewMode === 'single' ? 'white' : (theme ? "#64748b" : "#94a3b8"),
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Single Stage
                  </Text>
                </Pressable>
              </XStack>

              {/* Stage Filter */}
              {viewMode === 'single' && campaignData?.stages && (
                <View>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginBottom: 8
                  }}>
                    Select Stage to View:
                  </Text>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ paddingRight: 20 }}
                  >
                    <XStack gap={8}>
                      {campaignData.stages.map((stage, index) => (
                        <Pressable
                          key={stage._id}
                          onPress={() => setSelectedStageIndex(index)}
                          style={{
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderRadius: 6,
                            backgroundColor: selectedStageIndex === index
                              ? (theme ? "#dbeafe" : "#1e40af")
                              : (theme ? "#f1f5f9" : "#374151"),
                            borderWidth: 1,
                            borderColor: selectedStageIndex === index
                              ? (theme ? "#3b82f6" : "#60a5fa")
                              : (theme ? "#e2e8f0" : "#475569"),
                            minWidth: 100
                          }}
                        >
                          <Text style={{
                            color: selectedStageIndex === index
                              ? (theme ? "#1e40af" : "#93c5fd")
                              : (theme ? "#64748b" : "#94a3b8"),
                            fontSize: 11,
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            {parseByDelimiter(stage.title)}
                          </Text>
                          <Text style={{
                            color: selectedStageIndex === index
                              ? (theme ? "#1e40af" : "#93c5fd")
                              : (theme ? "#94a3b8" : "#64748b"),
                            fontSize: 10,
                            textAlign: 'center',
                            marginTop: 2
                          }}>
                            {campaignData.audience_data?.[index]?.length || 0} leads
                          </Text>
                        </Pressable>
                      ))}
                    </XStack>
                  </ScrollView>
                </View>
              )}

              {/* Pagination Controls */}
              {paginationInfo && (
                <View style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTopWidth: 1,
                  borderTopColor: theme ? "#e2e8f0" : "#475569"
                }}>
                  <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {paginationInfo.totalCount} total leads
                    </Text>
                  </XStack>
                  <XStack gap={8} justifyContent="center">
                    <Pressable
                      onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={!paginationInfo.hasPrevPage}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 6,
                        backgroundColor: paginationInfo.hasPrevPage
                          ? (theme ? "#3b82f6" : "#60a5fa")
                          : (theme ? "#e2e8f0" : "#475569"),
                        opacity: paginationInfo.hasPrevPage ? 1 : 0.5
                      }}
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={16}
                        color={paginationInfo.hasPrevPage ? 'white' : (theme ? "#64748b" : "#94a3b8")}
                      />
                    </Pressable>

                    <Pressable
                      onPress={() => setCurrentPage(Math.min(paginationInfo.totalPages, currentPage + 1))}
                      disabled={!paginationInfo.hasNextPage}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 6,
                        backgroundColor: paginationInfo.hasNextPage
                          ? (theme ? "#3b82f6" : "#60a5fa")
                          : (theme ? "#e2e8f0" : "#475569"),
                        opacity: paginationInfo.hasNextPage ? 1 : 0.5
                      }}
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={16}
                        color={paginationInfo.hasNextPage ? 'white' : (theme ? "#64748b" : "#94a3b8")}
                      />
                    </Pressable>
                  </XStack>
                </View>
              )}
            </View>

            {/* Dev Controls - Only show in development */}
            {__DEV__ && (
              <View style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: theme ? "#fef3c7" : "#451a03",
                borderRadius: 8,
                borderWidth: 1,
                borderColor: theme ? "#f59e0b" : "#fbbf24"
              }}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: theme ? "#92400e" : "#fbbf24",
                  marginBottom: 8
                }}>
                  DEV CONTROLS
                </Text>
                <XStack gap={8} flexWrap="wrap">
                  <Pressable
                    onPress={() => {
                        if (addStageMutation.isPending) {
                          Alert.alert('Please wait', 'Stage is being added...');
                          return;
                        }
                        if (!params?.campaignId) {
                          Alert.alert('Error', 'Campaign ID is missing');
                          return;
                        }
                      const stageNames = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
                      const randomName = stageNames[Math.floor(Math.random() * stageNames.length)];
                      const stageData = {
                        title: randomName,
                        description: `Auto-generated ${randomName} stage for testing`,
                        color: theme ? "#3b82f6" : "#60a5fa"
                      };
                        console.log('Adding stage with data:', stageData, 'campaignId:', params.campaignId);
                        addStageMutation.mutate(stageData, {
                          onError: (error) => {
                            console.error('Add stage mutation error:', error);
                          }
                        });
                    }}
                      disabled={addStageMutation.isPending}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      backgroundColor: addStageMutation.isPending
                        ? (theme ? "#94a3b8" : "#475569")
                        : (theme ? "#10b981" : "#059669"),
                      borderRadius: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      opacity: addStageMutation.isPending ? 0.6 : 1
                    }}
                  >
                    <MaterialIcons name="add" size={16} color="white" />
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                        {addStageMutation.isPending ? 'Adding...' : 'Add Stage'}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={() => {
                        if (deleteStageMutation.isPending) {
                          Alert.alert('Please wait', 'Stage is being deleted...');
                          return;
                        }
                        if (!params?.campaignId) {
                          Alert.alert('Error', 'Campaign ID is missing');
                          return;
                        }
                      if (campaignData?.stages && campaignData.stages.length > 1) {
                        const lastStage = campaignData.stages[campaignData.stages.length - 1];
                        Alert.alert(
                          'Delete Stage',
                          `Are you sure you want to delete "${lastStage.title}"?`,
                          [
                            { text: 'Cancel', style: 'cancel' },
                            {
                              text: 'Delete',
                              style: 'destructive',
                              onPress: () => {
                                console.log('Deleting stage:', lastStage._id, 'campaignId:', params.campaignId);
                                deleteStageMutation.mutate(lastStage._id, {
                                  onError: (error) => {
                                    console.error('Delete stage mutation error:', error);
                                  }
                                });
                              }
                            }
                          ]
                        );
                      } else {
                        Alert.alert('Cannot Delete', 'At least one stage must remain');
                      }
                    }}
                      disabled={deleteStageMutation.isPending}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      backgroundColor: deleteStageMutation.isPending
                        ? (theme ? "#94a3b8" : "#475569")
                        : (theme ? "#ef4444" : "#dc2626"),
                      borderRadius: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      opacity: deleteStageMutation.isPending ? 0.6 : 1
                    }}
                  >
                    <MaterialIcons name="remove" size={16} color="white" />
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                        {deleteStageMutation.isPending ? 'Deleting...' : 'Remove Last Stage'}
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={() => {
                      const testLead = {
                        name: `Test Lead ${Math.floor(Math.random() * 1000)}`,
                        email: `test${Math.floor(Math.random() * 1000)}@example.com`,
                        phone: `+1-555-${Math.floor(Math.random() * 9000) + 1000}`
                      };
                      addTestLeadMutation.mutate(testLead);
                    }}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      backgroundColor: theme ? "#8b5cf6" : "#7c3aed",
                      borderRadius: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <MaterialIcons name="person-add" size={16} color="white" />
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                      Add Test Lead
                    </Text>
                  </Pressable>
                </XStack>
              </View>
            )}

            {/* Stats Grid */}
            <XStack gap={8} flexWrap="wrap" justifyContent="center" alignItems="center">
              {[
                {
                  title: 'Total Leads',
                  value: campaignStats.totalLeads.toString(),
                  icon: 'people',
                  color: theme ? "#0ea5e9" : "#38bdf8",
                  bgColor: theme ? "#dbeafe" : "#0c4a6e"
                },
                {
                  title: 'Conversions',
                  value: campaignStats.actualConversions.toString(),
                  icon: 'check-circle',
                  color: theme ? "#059669" : "#10b981",
                  bgColor: theme ? "#d1fae5" : "#064e3b"
                },
                {
                  title: 'Conversion Rate',
                  value: `${campaignStats.conversionRate}%`,
                  icon: 'trending-up',
                  color: theme ? "#7c3aed" : "#a78bfa",
                  bgColor: theme ? "#ede9fe" : "#312e81"
                },
                {
                  title: 'Target Achievement',
                  value: `${campaignStats.targetAchievement}%`,
                  icon: 'flag',
                  color: theme ? "#dc2626" : "#ef4444",
                  bgColor: theme ? "#fee2e2" : "#7f1d1d"
                },
                {
                  title: 'Revenue',
                  value: `$${campaignStats.revenue.toLocaleString()}`,
                  icon: 'attach-money',
                  color: theme ? "#f59e0b" : "#fbbf24",
                  bgColor: theme ? "#fef3c7" : "#451a03"
                },
                {
                  title: 'ROI',
                  value: `${campaignStats.roi}%`,
                  icon: 'show-chart',
                  color: theme ? "#10b981" : "#34d399",
                  bgColor: theme ? "#d1fae5" : "#064e3b"
                },
                {
                  title: 'Emails Sent',
                  value: campaignStats.totalEmails.toString(),
                  icon: 'send',
                  color: theme ? "#06b6d4" : "#22d3ee",
                  bgColor: theme ? "#cffafe" : "#083344"
                },
                {
                  title: 'Bounce Rate',
                  value: `${campaignStats.bounceRate}%`,
                  icon: 'email',
                  color: theme ? "#ef4444" : "#f87171",
                  bgColor: theme ? "#fee2e2" : "#7f1d1d"
                }
              ].map((stat, index) => (
                <Animatable.View
                  key={index}
                  animation="bounceIn"
                  delay={index * 200}
                  style={{
                    flexBasis: Platform.OS === 'web' ? '30%' : '45%',
                    flexGrow: 1,
                    minWidth: 140,
                    backgroundColor: stat.bgColor,
                    borderRadius: 16,
                    padding: 16,
                    marginBottom: 8,
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                  }}
                >
                  <View style={{ alignItems: 'center' }}>
                    <MaterialIcons
                      name={stat.icon as any}
                      size={24}
                      color={stat.color}
                      style={{ marginBottom: 8 }}
                    />
                    <Text style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: stat.color,
                      marginBottom: 4
                    }}>
                      {stat.value}
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8",
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {stat.title}
                    </Text>
                  </View>
                </Animatable.View>
              ))}
            </XStack>

            {/* Campaign Progress Bar */}
            <View style={{
              marginTop: 16,
              padding: 16,
              backgroundColor: theme ? "#f8fafc" : "#334155",
              borderRadius: 12,
              borderWidth: 1,
              borderColor: theme ? "#e2e8f0" : "#475569"
            }}>
              <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? "#1e293b" : "#f1f5f9"
                }}>
                  Campaign Progress
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme ? "#64748b" : "#94a3b8"
                }}>
                  {campaignStats.elapsedDays} / {campaignStats.totalDays} days
                </Text>
              </XStack>
              <View style={{
                height: 8,
                backgroundColor: theme ? "#e2e8f0" : "#475569",
                borderRadius: 4,
                overflow: 'hidden'
              }}>
                <View style={{
                  height: '100%',
                  width: `${parseFloat(campaignStats.progressPercentage.toString())}%`,
                  backgroundColor: theme ? "#3b82f6" : "#60a5fa",
                  borderRadius: 4
                }} />
              </View>
              <XStack justifyContent="space-between" marginTop={6}>
                <Text style={{
                  fontSize: 10,
                  color: theme ? "#64748b" : "#94a3b8"
                }}>
                  Started: {campaignStats.startDate}
                </Text>
                <Text style={{
                  fontSize: 10,
                  color: theme ? "#64748b" : "#94a3b8"
                }}>
                  Ends: {campaignStats.endDate}
                </Text>
              </XStack>
            </View>

            {/* Stage Distribution */}
            {campaignStats.stageDistribution && campaignStats.stageDistribution.length > 0 && (
              <View style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: theme ? "#f8fafc" : "#334155",
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? "#e2e8f0" : "#475569"
              }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? "#1e293b" : "#f1f5f9",
                  marginBottom: 12,
                  textAlign: 'center'
                }}>
                  Lead Distribution by Stage
                </Text>
                <XStack gap={8} flexWrap="wrap" justifyContent="center">
                  {campaignStats.stageDistribution.map((stage, index) => (
                    <View
                      key={index}
                      style={{
                        flex: 1,
                        minWidth: 100,
                        backgroundColor: theme ? "#ffffff" : "#1e293b",
                        borderRadius: 8,
                        padding: 10,
                        alignItems: 'center',
                        borderWidth: 1,
                        borderColor: theme ? "#e2e8f0" : "#475569"
                      }}
                    >
                      <Text style={{
                        fontSize: 12,
                        fontWeight: '600',
                        color: theme ? "#1e293b" : "#f1f5f9",
                        marginBottom: 4,
                        textAlign: 'center'
                      }}>
                        {stage.title}
                      </Text>
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '700',
                        color: theme ? "#3b82f6" : "#60a5fa",
                        marginBottom: 2
                      }}>
                        {stage.count}
                      </Text>
                      <Text style={{
                        fontSize: 10,
                        color: theme ? "#64748b" : "#94a3b8"
                      }}>
                        {stage.percentage}%
                      </Text>
                    </View>
                  ))}
                </XStack>
              </View>
            )}

            {/* Campaign Metadata */}
            <View style={{
              marginTop: 16,
              padding: 16,
              backgroundColor: theme ? "#f8fafc" : "#334155",
              borderRadius: 12,
              borderWidth: 1,
              borderColor: theme ? "#e2e8f0" : "#475569"
            }}>
              <Text style={{
                fontSize: 14,
                fontWeight: '600',
                color: theme ? "#1e293b" : "#f1f5f9",
                marginBottom: 12
              }}>
                Campaign Details
              </Text>
              <XStack gap={16} flexWrap="wrap">
                <View style={{ flex: 1, minWidth: 150 }}>
                  <Text style={{
                    fontSize: 11,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginBottom: 4
                  }}>
                    Marketing Scope
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9"
                  }}>
                    {campaignStats.marketingScope}
                  </Text>
                </View>
                <View style={{ flex: 1, minWidth: 150 }}>
                  <Text style={{
                    fontSize: 11,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginBottom: 4
                  }}>
                    Goals
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9"
                  }}>
                    {campaignStats.goals}
                  </Text>
                </View>
                <View style={{ flex: 1, minWidth: 150 }}>
                  <Text style={{
                    fontSize: 11,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginBottom: 4
                  }}>
                    Sales Target
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9"
                  }}>
                    {campaignStats.salesTarget} conversions
                  </Text>
                </View>
              </XStack>
              {campaignStats.description && (
                <View style={{ marginTop: 12 }}>
                  <Text style={{
                    fontSize: 11,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginBottom: 4
                  }}>
                    Description
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#1e293b" : "#f1f5f9",
                    lineHeight: 16
                  }}>
                    {campaignStats.description}
                  </Text>
                </View>
              )}
            </View>
            </ScrollView>
        )}
        </View>
      </Animatable.View>
    );
  }, [campaignStats, campaignData, theme, addStageMutation, deleteStageMutation, addTestLeadMutation, queryClient, params?.campaignId, isStatsExpanded]);

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <Card
          style={[
            styles.loadingContainer,
            {
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
            }
          ]}
        >
          <AnimatedLottieView
            source={require('@assets/animations/leaf_Loader.json')}
            style={styles.loader}
            autoPlay
            loop
          />
          <Text style={[styles.loadingText, { color: theme ? "#64748b" : "#94a3b8" }]}>
            Loading Campaign Data
          </Text>
          <Spinner />
        </Card>
      );
    }

    return (
      <Card
        style={[
          styles.funnelCard,
          {
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }
        ]}
      >
        <SalesFunnelList
          ref={funnelRef}
          clients={clients}
          salesFunnel={campaignData}
          viewMode={viewMode}
          selectedStageIndex={selectedStageIndex}
          onMoveLead={(leadId, fromStage, toStage) => {
            moveLeadMutation.mutate({ leadId, fromStage, toStage });
          }}
          onUpdateLead={(leadId, leadData) => {
            updateLeadMutation.mutate({ leadId, leadData });
          }}
          onDeleteLead={(leadId) => {
            deleteLeadMutation.mutate({ leadId });
          }}
        />
      </Card>
    );
  }, [isLoading, clients, campaignData, theme, moveLeadMutation, updateLeadMutation, deleteLeadMutation, viewMode, selectedStageIndex]);

  return (
    <DefaultScreen
      backButton
      scrollDisabled
      noMargin
      searchBarStyle={{
        borderRadius: 0,
        marginHorizontal: -10,
      }}
      style={styles.defaultScreen}
      searchBar={{
        placeHolder: "Search for a client...",
        searchState: searchText,
        setSearchState: setSearchText
      }}
    >
      <View style={styles.container}>
        {/* Sales Funnel Content */}
        <View style={styles.funnelContainer}>
          {renderContent()}
        </View>

        {/* Campaign Statistics Cards - Absolute at Bottom */}
        {HeaderComponent()}
      </View>
    </DefaultScreen>
  );
};

const styles = StyleSheet.create({
  defaultScreen: {
    paddingRight: 0,
  },
  container: {
    flex: 1,
    alignSelf: "stretch",
    paddingRight: Platform.select({ web: 50, default: 0 }),
    position: 'relative',
  },
  funnelContainer: {
    height: Dimensions.get('window').height * 0.7,
    minHeight: Dimensions.get('window').height * 0.7,
    marginHorizontal: 20,
    marginBottom: 20,
  },
  funnelCard: {
    flex: 1,
    borderRadius: 20,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 10,
    borderWidth: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 15,
    padding: 50,
    paddingTop: 150,
    borderRadius: 20,
    shadowRadius: 20,
    shadowOpacity: 0.2,
    margin: 150,
    maxHeight: 350,
    borderWidth: 1,
  },
  loader: {
    flex: 3,
    minHeight: 250,
    height: 250,
    width: 250,
  },
  loadingText: {
    flex: 1,
    textAlign: "center",
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SalesFunnel;
